import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { Form, Container, Text, Spacer, FormSerialization, Button } from 'roadie-ui'
import Avatar from 'rui/widgets/Avatar'
import Page from 'rui/widgets/Page'
import DriveSidebar from 'components/layout/DriveSidebar'
import Thin from 'components/layout/Thin'
import Step from 'components/misc/Step'
import { updatePhoto, updateProfile } from 'actions/profile'
import Photo from 'components/form/Photo'
import { useDispatch } from 'hooks/useDispatch'
import { Profile } from 'types/profile'
import avatar from 'images/avatar.png'
import { sendNativeMessage } from 'util/native'
import { getProfilePhoto } from 'util/profile'
import { formatRegistrationStep } from 'util/registrationFlow'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'

const PhotoAndBioPage: React.FC<{
  registered: boolean
}> = (props) => {
  const { t } = useLocalization()
  const { registered } = props
  const dispatch = useDispatch()
  const { profile, isWebview, isLocked } = useSelector<
    any,
    {
      profile: Profile
      isWebview: boolean
      isLocked: boolean
    }
  >((state) => ({
    profile: state.profile,
    isWebview: state.config.isWebview,
    isLocked: state.isLocked,
  }))
  const [file, setFile] = React.useState<File>()
  const registrationStep = formatRegistrationStep('PHOTO_AND_BIO', profile)

  const handleData = React.useCallback(
    (data: FormSerialization) => {
      if (!registered && !file) {
        return Promise.reject({
          response: { data: { errors: { base: t('dweb_photo_and_bio_select_photo') } } },
        })
      } else {
        const firstStep = file ? dispatch(updatePhoto(file)) : Promise.resolve()
        return firstStep
          .then(() => {
            if (profile.biography !== data.biography) {
              return dispatch(updateProfile({ profile: data }))
            }
          })
          .then(() => {
            if (registered) {
              dispatch({
                type: 'PUSH_FLASH',
                flash: {
                  messageKey: 'dweb_flash_profile_changes_saved',
                  type: 'success',
                },
              })
            }
          })
      }
    },
    [dispatch, file, profile.biography, registered, t]
  )
  const handleChange = React.useCallback((res: { file: File }) => {
    if (res) {
      setFile(res.file)
    }
  }, [])
  const handleClick = React.useCallback((event: InputEvent) => {
    event.preventDefault()
    sendNativeMessage({ type: 'UPDATE_PHOTO' })
  }, [])

  const pageTitle = !registered || isWebview ? 'Profile Picture' : 'Photo & Bio'

  return (
    <Page sidebar={!registered ? undefined : <DriveSidebar />}>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Thin>
        <Form.Basic id="photo-and-bio-form" onSubmit={handleData}>
          <Container.Panel $textAlign="center">
            <Text.H4 $color="neutral-black">{pageTitle}</Text.H4>
            <Spacer $height="3.2rem" />
            <Photo
              id="profile-photo"
              defaultSrc={getProfilePhoto(profile)} // Show verified avatar if exists
              disabled={!isWebview || isLocked}
              onChange={isWebview ? handleChange : undefined}
              onClick={isWebview ? handleClick : undefined}
            >
              {(src: string) => (
                <>
                  <Avatar url={src || avatar} size={140} />
                  <Spacer $height="2.4rem" />
                  {isWebview ? (
                    isLocked ? (
                      <Button.Secondary $width="100%" disabled>
                        <LocalizedText translationKey="dweb_account_photo_and_bio_add_photo" />
                      </Button.Secondary>
                    ) : (
                      <Button.Div.Secondary $width="100%">{src ? 'Take New Photo' : 'Add Photo'}</Button.Div.Secondary>
                    )
                  ) : (
                    <>
                      <Text.Body1 $color="neutral-black">
                        <LocalizedRichText
                          translationKey="dweb_account_photo_and_bio_please_login_strong_text1_app_update_profile_photo"
                          params={{
                            strongText1: (
                              <strong>
                                <LocalizedText translationKey="dweb_go_mobile_roadie_driver" />
                              </strong>
                            ),
                          }}
                        />
                      </Text.Body1>
                      <Spacer $height="1.6rem" />
                    </>
                  )}
                </>
              )}
            </Photo>
            {registered ? (
              <>
                <Spacer $height="1.6rem" />
                <Form.Input
                  type="text"
                  name="biography"
                  label={t('dweb_account_photo_and_bio_tell_us_about_yourself')}
                  defaultValue={profile.biography ?? ''}
                  disabled={isLocked}
                />
                <Spacer $height="2.4rem" />
              </>
            ) : null}
            <Spacer $height="2.4rem" />
            {isLocked ? (
              <Button.Primary $borderWidth="0" $width="100%" disabled={true}>
                {!registered ? 'Next' : 'Save'}
              </Button.Primary>
            ) : (
              <Form.Submit>{!registered ? 'Next' : 'Save'}</Form.Submit>
            )}
            {!registered && registrationStep ? <Step>{registrationStep}</Step> : null}
          </Container.Panel>
        </Form.Basic>
      </Thin>
    </Page>
  )
}

export default PhotoAndBioPage
